<template>
  <div>
    <!-- Header -->
    <div class="d-flex flex-row mb-6 align-center">
      <div>
        <p class="text-2xl mb-0 primary--text font-weight-bold">Contextes de Prompts</p>
        <p class="text-sm grey--text mb-0">Définition des points d'entrée (Entrypoints) pour le moteur LLPR</p>
      </div>
      <v-spacer></v-spacer>
      <v-btn color="primary" @click="openContextDialog()">
        <v-icon left>{{ icons.mdiPlus }}</v-icon>
        Nouveau Contexte
      </v-btn>
    </div>

    <v-card shadow="none" class="elevation-1">
      <v-data-table
        :headers="headers"
        :items="contexts"
        :loading="loading"
        class="elevation-0"
      >
        <template #[`item.code`]="{ item }">
          <code class="font-weight-bold primary--text">{{ item.code }}</code>
        </template>
        
        <template #[`item.steps`]="{ item }">
          <span v-if="item.steps && item.steps.length" class="font-weight-bold">{{ item.steps.length }}</span>
        </template>

        <template #[`item.created`]="{ item }">
          {{ new Date(item.created).toLocaleDateString() }}
        </template>

        <template #[`item.actions`]="{ item }">
          <div class="d-flex justify-end">
            <v-btn icon small @click="openContextDialog(item)" color="primary">
              <v-icon small>{{ icons.mdiPencil }}</v-icon>
            </v-btn>
            <v-btn v-if="showDevActions" icon small @click="confirmDeleteContext(item)" color="error">
              <v-icon small>{{ icons.mdiTrashCan }}</v-icon>
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Modal Formulaire Context -->
    <v-dialog v-model="dialog" max-width="900px">
      <v-card v-if="form">
        <v-card-title class="headline grey lighten-2">
          {{ isEditing ? 'Modifier le contexte : ' + form.code : 'Nouveau contexte' }}
        </v-card-title>
        <v-card-text class="pt-6">
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.code"
                label="Code (Technique)"
                placeholder="Ex: GEO_SNIPPET"
                outlined
                dense
                :disabled="isEditing && !showDevActions"
                hint="Le code utilisé dans le code source"
                persistent-hint
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.label"
                label="Label (Affichage)"
                placeholder="Ex: GEO Campaign - Snippet creation"
                outlined
                dense
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- STEPS MANAGEMENT (Inside Dialog) -->
          <template v-if="isEditing">
            <v-divider class="my-4"></v-divider>
            <div class="d-flex align-center mb-4">
              <span class="text-h6">Gestion des Étapes</span>
              <v-spacer></v-spacer>
              <v-btn small color="success" @click="openStepDialog(form)">
                <v-icon left small>{{ icons.mdiPlus }}</v-icon>
                Ajouter une étape
              </v-btn>
            </div>

            <v-simple-table dense class="elevation-1 rounded mb-4">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left" style="width: 100px">Ordre</th>
                    <th class="text-left">Key (Technique)</th>
                    <th class="text-left">Label (Affichage)</th>
                    <th class="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(step, index) in form.steps" :key="step.id">
                    <td>
                      <v-btn icon x-small :disabled="index === 0" @click="moveStep(form, index, -1)">
                        <v-icon small>{{ icons.mdiArrowUp }}</v-icon>
                      </v-btn>
                      <v-btn icon x-small :disabled="index === form.steps.length - 1" @click="moveStep(form, index, 1)">
                        <v-icon small>{{ icons.mdiArrowDown }}</v-icon>
                      </v-btn>
                    </td>
                    <td><code>{{ step.step_key }}</code></td>
                    <td>{{ step.label }}</td>
                    <td class="text-right">
                      <v-btn icon x-small color="primary" @click="openStepDialog(form, step)">
                        <v-icon small>{{ icons.mdiPencil }}</v-icon>
                      </v-btn>
                      <v-btn icon x-small color="error" @click="confirmDeleteStep(form, step)">
                        <v-icon small>{{ icons.mdiTrashCan }}</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                  <tr v-if="!form.steps || !form.steps.length">
                    <td colspan="4" class="text-center grey--text py-4">Aucune étape définie</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </template>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialog = false">Fermer</v-btn>
          <v-btn color="primary" @click="save" :loading="saving" :disabled="!form.code || !form.label">Enregistrer le contexte</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal Formulaire Step -->
    <v-dialog v-model="stepDialog" max-width="400px">
      <v-card>
        <v-card-title class="headline grey lighten-2">
          {{ isEditingStep ? 'Modifier l\'étape' : 'Nouvelle étape' }}
        </v-card-title>
        <v-card-text class="pt-6">
          <v-text-field
            v-model="stepForm.step_key"
            label="Key (Technique)"
            placeholder="Ex: table"
            outlined dense
            :disabled="isEditingStep"
          ></v-text-field>
          <v-text-field
            v-model="stepForm.label"
            label="Label (Affichage)"
            placeholder="Ex: Tableau comparatif"
            outlined dense
          ></v-text-field>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="stepDialog = false">Annuler</v-btn>
          <v-btn color="primary" @click="saveStep" :loading="savingStep">Enregistrer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from '@vue/composition-api'
import { mdiPlus, mdiPencil, mdiTrashCan, mdiArrowUp, mdiArrowDown } from '@mdi/js'
import { Konami, KonamiManager } from "@/utils/konami";
import llprService from '@/services/llpr.service'
import store from '@/store'

export default {
  name: 'PromptContextsList',
  setup() {
    const loading = ref(false)
    const saving = ref(false)
    const contexts = ref([])
    const showDevActions = ref(false)
    let konamiManager = null

    // Context Form
    const dialog = ref(false)
    const isEditing = ref(false)
    const originalCode = ref('')
    const form = ref({ code: '', label: '' })

    // Step Form
    const stepDialog = ref(false)
    const isEditingStep = ref(false)
    const savingStep = ref(false)
    const selectedContext = ref(null)
    const stepForm = ref({ id: null, step_key: '', label: '', order_index: 0 })

    const headers = [
      { text: 'Code Technique', value: 'code' },
      { text: 'Description / Label', value: 'label' },
      { text: 'Étapes', value: 'steps', sortable: false },
      { text: 'Créé le', value: 'created' },
      { text: 'Actions', value: 'actions', align: 'end', sortable: false },
    ]

    const fetchData = async () => {
      loading.value = true
      try {
        const res = await llprService.getContexts()
        contexts.value = res.data
        
        // If we are currently editing, update the form to reflect latest steps
        if (dialog.value && form.value && form.value.code) {
          const current = res.data.find(c => c.code === form.value.code)
          if (current) {
            form.value = { ...current }
          }
        }
      } catch (e) {
        console.error(e)
      } finally {
        loading.value = false
      }
    }

    // --- CONTEXT ACTIONS ---
    const openContextDialog = (item = null) => {
      isEditing.value = !!item
      if (item) {
        form.value = { ...item }
        originalCode.value = item.code
      } else {
        form.value = { code: '', label: '' }
        originalCode.value = ''
      }
      dialog.value = true
    }

    const save = async () => {
      saving.value = true
      try {
        if (isEditing.value) {
          await llprService.updateContext(originalCode.value, form.value)
        } else {
          await llprService.createContext(form.value)
        }
        store.dispatch('app/setSnackBar', { show: true, text: 'Contexte enregistré', color: 'success' })
        dialog.value = false
        fetchData()
      } catch (e) {
        const detail = e.response?.data?.detail
        const msg = typeof detail === 'string' ? detail : JSON.stringify(detail || 'Erreur lors de la sauvegarde')
        store.dispatch('app/setSnackBar', { 
          show: true, 
          text: msg, 
          color: 'error' 
        })
      } finally {
        saving.value = false
      }
    }

    const confirmDeleteContext = async (item) => {
      if (confirm(`Voulez-vous vraiment supprimer le contexte "${item.code}" ?`)) {
        try {
          await llprService.deleteContext(item.code)
          store.dispatch('app/setSnackBar', { show: true, text: 'Contexte supprimé', color: 'success' })
          fetchData()
        } catch (e) {
          store.dispatch('app/setSnackBar', { 
            show: true, 
            text: e.response?.data?.detail || 'Erreur lors de la suppression (il est sûrement utilisé)', 
            color: 'error' 
          })
        }
      }
    }

    // --- STEP ACTIONS ---
    const openStepDialog = (context, step = null) => {
      selectedContext.value = context
      isEditingStep.value = !!step
      if (step) {
        stepForm.value = { ...step }
      } else {
        stepForm.value = { 
          id: null, 
          step_key: '', 
          label: '', 
          order_index: context.steps.length + 1 
        }
      }
      stepDialog.value = true
    }

    const saveStep = async () => {
      savingStep.value = true
      try {
        if (isEditingStep.value) {
          await llprService.updateContextStep(stepForm.value.id, stepForm.value)
        } else {
          await llprService.createContextStep(selectedContext.value.code, stepForm.value)
        }
        store.dispatch('app/setSnackBar', { show: true, text: 'Étape enregistrée', color: 'success' })
        stepDialog.value = false
        fetchData()
      } catch (e) {
        store.dispatch('app/setSnackBar', { 
          show: true, 
          text: e.response?.data?.detail || 'Erreur lors de la sauvegarde', 
          color: 'error' 
        })
      } finally {
        savingStep.value = false
      }
    }

    const confirmDeleteStep = async (context, step) => {
      if (confirm(`Supprimer l'étape "${step.label}" ?`)) {
        try {
          await llprService.deleteContextStep(step.id)
          fetchData()
        } catch (e) {
          store.dispatch('app/setSnackBar', { show: true, text: 'Erreur lors de la suppression', color: 'error' })
        }
      }
    }

    const moveStep = async (context, index, delta) => {
      const newIndex = index + delta
      if (newIndex < 0 || newIndex >= context.steps.length) return

      // Local swap
      const steps = [...context.steps]
      const temp = steps[index]
      steps[index] = steps[newIndex]
      steps[newIndex] = temp

      // Update local state for immediate feedback
      context.steps = steps

      // Sync with backend
      try {
        const stepIds = steps.map(s => s.id)
        await llprService.reorderContextSteps(context.code, stepIds)
      } catch (e) {
        console.error('Failed to reorder:', e)
        fetchData() // Revert on error
      }
    }

    onMounted(() => {
      fetchData()
      const devKonami = new Konami([68, 69, 86], () => {
        showDevActions.value = !showDevActions.value
        store.dispatch('app/setSnackBar', { 
          show: true, 
          text: `Mode Dev ${showDevActions.value ? 'activé' : 'désactivé'}`, 
          color: 'info' 
        })
      })
      konamiManager = new KonamiManager(null, [devKonami])
    })

    onUnmounted(() => {
      if (konamiManager) konamiManager.destroy()
    })

    return {
      loading, saving, contexts, headers,
      dialog, isEditing, form, showDevActions,
      stepDialog, isEditingStep, savingStep, stepForm,
      openContextDialog, save, confirmDeleteContext,
      openStepDialog, saveStep, confirmDeleteStep, moveStep,
      icons: { mdiPlus, mdiPencil, mdiTrashCan, mdiArrowUp, mdiArrowDown }
    }
  }
}
</script>

<style scoped>
.text-gradient {
  background: linear-gradient(45deg, #43a047, #1de9b6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 800;
}
</style>
